import {
	ConnectionFormField,
	ConnStrReplacer,
} from '@Utils/connectionStringReplacer';
import {
	ConnectionTypeOptions,
	DateFormatOptions,
	ServerTypeOptions,
	QuoteFormatOptions,
	TimestampConversionOptions,
	TagFieldFormatOptions,
	ServerTypeOptionsOledb,
} from '@Types/systemConnections.types';
import { SelectChangeEvent } from '@mui/material';
import React, { useMemo } from 'react';
import { timezones } from '@Utils/timeZones';
import { OpcUaFields } from './OpcUaFields';
import { SECURITY_MODES, SECURITY_POLICIES } from './OpcuaTypes';
import { OleDbFields } from './OleDbFields';
import { OdbcFields } from './OdbcFields';
import { useSystemConnectionDriver } from '@Hooks';

const TZ_OPTIONS = timezones.map(key => ({
	key,
	value: key.replace(/_/g, ' '),
}));

const TZ_DEFAULT = TZ_OPTIONS.find(x => x.key == 'UTC');

export const NewSystemConnectionFormConfigurations = {
	serverTypeOptions: ServerTypeOptions,
	serverTypeOptionsOledb: ServerTypeOptionsOledb,
	connectionTypeOptions: ConnectionTypeOptions,
	connectionString: `DRIVER=${ServerTypeOptions[0]};PORT=1433;`,
	connectionStringOledb: `Provider=${ServerTypeOptionsOledb[0]};`,
};

const COMMON_FIELDS = {
	databaseTimezone: TZ_DEFAULT?.value || '',
	sampleDurationSeconds: '300',
	queryLagMinutes: '30',
	queryGroupSize: '1000',
	caseSensitiveSensorId: false.toString(),
	simulationSubtractionDays: '0',
};

const COMMON_FIELDS_ODBC = {
	...COMMON_FIELDS,
	timeout: '30',
	port: '1433',
	dateTimeFormat: DateFormatOptions.ANSI.value,
	quoteFormat: QuoteFormatOptions.DOUBLE_QUOTE.value,
	timestampConversion: TimestampConversionOptions.IMPLICIT.value,
	tagFormat: TagFieldFormatOptions.STRING.value,
	connectionString: '',
	serverType: '',
};
const COMMON_FIELDS_OLEDB = {
	...COMMON_FIELDS_ODBC,
	port: '',
	queryPrefix: '',
};

const COMMON_FIELDS_OPCUA = {
	...COMMON_FIELDS,
	port: '53530',
	serverAddress: '',
	resourcePath: '',
	opcuaUrl: '',
};

export interface NewSystemConnectionFormProps {
	updateFormData: (p: Record<string, string>, fromUser?: boolean) => void;
	formData: Record<string, string>;
	formErrors: Record<string, string>;
}

export interface SystemConnectionType {
	systemConnectionTypeName: string;
	systemConnectionTypeId: string;
}

export const NewSystemConnectionForm = (
	props: NewSystemConnectionFormProps,
) => {
	const {
		determineValuesFromServerType,
		systemConnectionTypes,
		buildOpcuaUrl,
	} = useSystemConnectionDriver();

	const defaultDateTimeFormatHandler = (
		props: NewSystemConnectionFormProps,
	) => {
		let dateTimeFormat = 'YYYY-MM-DD';
		if (props.formData?.serverType === 'ORACLE')
			dateTimeFormat = 'DD-MMM-YYYY';
		if (
			!props.formData?.dateTimeFormat ||
			props.formData?.dateTimeFormat === ''
		) {
			props.formData.dateTimeFormat = dateTimeFormat;
		}
	};

	const defaultQuoteFormatHandler = (props: NewSystemConnectionFormProps) => {
		let quoteFormat = 'DOUBLE_QUOTE';
		if (props.formData?.serverType === 'MYSQL') quoteFormat = 'BACK_TICK';
		if (
			!props.formData?.quoteFormat ||
			props.formData?.quoteFormat === ''
		) {
			props.formData.quoteFormat = quoteFormat;
		}
	};

	defaultDateTimeFormatHandler(props);
	defaultQuoteFormatHandler(props);

	const defaultDateTimeConversionHandler = (
		props: NewSystemConnectionFormProps,
	) => {
		let timestampConversion = 'IMPLICIT';
		if (props.formData?.serverType === 'Oracle')
			timestampConversion = 'TIMESTAMP_prefix';
		if (props.formData?.serverType === 'ClearSCADA')
			timestampConversion = 'TS_function';
		if (
			!props.formData?.timestampConversion ||
			props.formData?.timestampConversion === ''
		)
			props.formData.timestampConversion = timestampConversion;
	};
	const defaultTagFormatHandler = (props: NewSystemConnectionFormProps) => {
		let tagFormat = 'STRING';
		if (props.formData?.serverType === 'ClearSCADA') tagFormat = 'NUMBER';
		if (!props.formData?.tagFormat || props.formData?.tagFormat === '')
			props.formData.tagFormat = tagFormat;
	};
	defaultDateTimeConversionHandler(props);
	defaultTagFormatHandler(props);

	const changeServerTypeAutocompleteHandler =
		(name: ConnectionFormField) =>
		(_event: React.ChangeEvent<unknown>, newValue: string | null) => {
			handleServerTypeChange(name, newValue);
		};

	const refreshOPCUAUrl = () => {
		const opcuaUrl = buildOpcuaUrl(
			props.formData.serverAddress,
			props.formData.port,
			props.formData.resourcePath,
		);
		props.updateFormData({ opcuaUrl });
	};

	const textChanged = (event: React.FormEvent) => {
		const target = event?.target as HTMLInputElement;
		// special case for username and password to remove the placeholder dots
		if (target?.name === 'username' || target?.name === 'password') {
			const val = target.value.replace(/•/g, '');
			props.updateFormData({ [target?.name]: val }, true);
		} else {
			props.updateFormData(
				{
					[target?.name]: target?.value,
				},
				true,
			);
		}
	};

	const numberFieldTextChanged = (event: React.FormEvent) => {
		const target = event?.target as HTMLInputElement;
		const targetValue = parseInt(target?.value);
		const isValid = !isNaN(targetValue);
		const newValue = isValid ? `${targetValue}` : '';
		props.updateFormData({ [target?.name]: newValue }, true);
	};

	const handleConnectionTypeChange = (event: SelectChangeEvent<string>) => {
		const target = event?.target as HTMLInputElement;
		props.updateFormData(
			{
				[target?.name]: target?.value,
			},
			true,
		);
		Object.keys(props.formErrors).forEach(error => {
			delete props.formErrors[error];
		});

		if (props.formData.editMode === 'true') {
			if (target?.value === 'OPC-UA') {
				props.updateFormData({ connectionString: '' }, true);
				refreshOPCUAUrl();
			}
		}

		if (props.formData.editMode === 'false') {
			if (target?.value === 'ODBC') {
				defaultDateTimeFormatHandler(props);
				defaultQuoteFormatHandler(props);
				defaultDateTimeConversionHandler(props);
				defaultTagFormatHandler(props);
				props.updateFormData(
					{
						...COMMON_FIELDS_ODBC,
						serverType:
							NewSystemConnectionFormConfigurations
								.serverTypeOptions[0],
						connectionString:
							NewSystemConnectionFormConfigurations.connectionString,
					},
					true,
				);
			}
			if (target?.value === 'OLEDB') {
				defaultDateTimeFormatHandler(props);
				defaultQuoteFormatHandler(props);
				defaultDateTimeConversionHandler(props);
				defaultTagFormatHandler(props);
				props.updateFormData(
					{
						...COMMON_FIELDS_OLEDB,
						serverType:
							NewSystemConnectionFormConfigurations
								.serverTypeOptionsOledb[0],
						connectionString:
							NewSystemConnectionFormConfigurations.connectionStringOledb,
					},
					true,
				);
			}
			if (target?.value === 'OPC-UA') {
				props.updateFormData(
					{ ...COMMON_FIELDS_OPCUA, timeout: '0' },
					true,
				);
			}
		}
	};

	const handleSelectChange = (event: SelectChangeEvent<string>) => {
		const target = event?.target as HTMLInputElement;
		props.updateFormData({ [target?.name]: target?.value }, true);
	};

	const connectionStringTextChanged = (event: React.FormEvent) => {
		const { value } = event?.target as HTMLInputElement;
		props.updateFormData(
			{
				connectionString: ConnStrReplacer.stripProhibited(value),
			},
			true,
		);
	};

	const handleServerTypeChange = (
		name: ConnectionFormField,
		newValue: string | null,
	) => {
		const newFormData = determineValuesFromServerType(name, newValue);
		props.updateFormData(newFormData, true);
	};

	const renderODBCForm = () => {
		return (
			<OdbcFields
				updateFormData={props.updateFormData}
				formData={props.formData}
				formErrors={props.formErrors}
				tzOptions={TZ_OPTIONS}
				tzDefault={TZ_DEFAULT}
				systemConnectionTypes={sysConnTypes}
				handleConnectionTypeChange={handleConnectionTypeChange}
				handleServerTypeChange={changeServerTypeAutocompleteHandler(
					'serverType',
				)}
				connectionStringTextChanged={connectionStringTextChanged}
				textFieldChanged={textChanged}
				numberFieldChanged={numberFieldTextChanged}
				handleSelectChange={handleSelectChange}
			/>
		);
	};

	const sysConnTypes = useMemo(() => systemConnectionTypes(), []);

	const renderOPCUAForm = () => {
		return (
			<OpcUaFields
				updateFormData={props.updateFormData}
				formData={props.formData}
				formErrors={props.formErrors}
				refreshOPCUAUrl={refreshOPCUAUrl}
				tzOptions={TZ_OPTIONS}
				tzDefault={TZ_DEFAULT}
				systemConnectionTypes={sysConnTypes}
				handleConnectionTypeChange={handleConnectionTypeChange}
				securityModes={SECURITY_MODES}
				securityPolicies={SECURITY_POLICIES}
			/>
		);
	};

	const renderOLEDBForm = () => {
		return (
			<OleDbFields
				updateFormData={props.updateFormData}
				formData={props.formData}
				formErrors={props.formErrors}
				tzOptions={TZ_OPTIONS}
				tzDefault={TZ_DEFAULT}
				systemConnectionTypes={sysConnTypes}
				handleConnectionTypeChange={handleConnectionTypeChange}
				handleServerTypeChange={changeServerTypeAutocompleteHandler(
					'serverType',
				)}
				connectionStringTextChanged={connectionStringTextChanged}
				textFieldChanged={textChanged}
				numberFieldChanged={numberFieldTextChanged}
				handleSelectChange={handleSelectChange}
			/>
		);
	};

	switch (props.formData.connectionType) {
		case 'ODBC':
			return renderODBCForm();
		case 'OPC-UA':
			return renderOPCUAForm();
		case 'OLEDB':
			return renderOLEDBForm();
		default:
			return renderODBCForm();
	}
};

export default NewSystemConnectionForm;
